import { StoryCharacterAgeUnitEnum, StoryCharacterEnum, StoryCharacterGenderEnum } from './character.interface';
import { FirebaseTimestamp } from './_common.interface';

export enum MidjourneyModeGeneration {
  relaxed = 'relaxed',
  fast = 'fast',
}

export enum BookImageGenerationMode {
  relaxed = 'relaxed',
  fast = 'fast',
  mixed = 'mixed',
}

export interface IPageDataImageModel {
  imageURL: string;
  imageURLwebp: string;
  pageKey: string;
}

export enum WritingStyleEnum {
  imaginative = 'imaginative',
  funny = 'funny',
  heartwarming = 'heartwarming',
  action_packed = 'action_packed',
  nostalgic = 'nostalgic',
  empowering = 'empowering',
  spooky = 'spooky',
  educational = 'educational',
}

export enum StoryTypeEnum {
  bedtime = 'bedtime',
  fable = 'fable',
  fairytale = 'fairytale',
  adventure = 'adventure',
  educational = 'educational',
  mystery = 'mystery',
  science_fiction = 'science_fiction',
  realistic_fiction = 'realistic_fiction',
}

export enum StoryTypeByAgeEnum {
  CHILD_AGE = 'CHILD_AGE',
  STORY = 'STORY',
  WRITING_STYLE = 'WRITING_STYLE',
  ART_STYLE = 'ART_STYLE'
}

export enum StoryAgeGroupTypeEnum {
  AGE_0_2 = '0-2-year',
  AGE_3_7 = '3-7-year',
  AGE_8_12 = '8-12-year',
  ADULT = 'adult'
}

export enum StoryAgeTitleEnum {
  '0-2-year' = 'Short',
  '3-7-year' = 'Medium',
  '8-12-year' = 'Long',
  'adult' = 'Extra Long'
}

/** Pedagogical order for Reading Level dropdowns (storyconfig key order from Firestore is not reliable). */
export const STORY_READING_LEVEL_KEYS_ORDER: readonly string[] = [
  StoryAgeGroupTypeEnum.AGE_0_2,
  StoryAgeGroupTypeEnum.AGE_3_7,
  StoryAgeGroupTypeEnum.AGE_8_12,
  StoryAgeGroupTypeEnum.ADULT,
];

export function getOrderedReadingLevelKeys(storyconfig: Record<string, unknown>): string[] {
  const ordered = STORY_READING_LEVEL_KEYS_ORDER.filter((key) =>
    Object.prototype.hasOwnProperty.call(storyconfig, key),
  );
  const skip = new Set(['id', 'ref', ...STORY_READING_LEVEL_KEYS_ORDER]);
  const rest = Object.keys(storyconfig).filter((key) => !skip.has(key));
  return [...ordered, ...rest];
}

export enum StoryLanguageTypeEnum {
  ENGLISH = 'english (US)',
  UK_ENGLISH = 'english (UK)',
  SPANISH = 'Spanish',
  FRENCH = 'french',
  GERMAN = 'german'
}

export enum StoryPurchaseTypeEnum {
  CREDITS = 'credits',
  STORY = 'story'
}

export interface IImageLibrary {
  previousImageURL: string;
  previousImageURLWebp: string;
  timestamp: Date;
}

export interface ICharacterOutlineByUser {
  age?: number;
  ageUnit?: StoryCharacterAgeUnitEnum;
  animalType?: string;
  characterType?: StoryCharacterEnum;
  gender?: StoryCharacterGenderEnum;
  mics?: string;
  character?: string;
}

export interface IPageContentFilterModel {
  flagged: true;
  category: string;
  blockedBy: string;
  guidance: string;
  flaggedAt?: any;
}

/**
 * Mirror of the server-side `ImageGenErrorCategory` (see
 * `.claude/skills/runware-errors/SKILL.md`). Drives the page-level
 * Retry/Edit UX bucketing.
 */
export type ImageGenErrorCategory =
  | 'moderation-content'
  | 'moderation-prompt'
  | 'provider-rate-limit'
  | 'provider-timeout'
  | 'provider-error'
  | 'infra-upload-failed'
  | 'infra-network'
  | 'infra-job-abandoned'
  | 'config-invalid-params'
  | 'config-insufficient-credits'
  | 'unknown';

export interface IPageDataModel {
  error?: boolean;
  errorCategory?: ImageGenErrorCategory | null;
  contentFilter?: IPageContentFilterModel | null;
  imageURL?: string;
  imageURLwebp?: string;
  job_id?: string;
  progress?: string;
  queueEstimatedTime?: number;
  queuePosition?: number;
  scene?: string;
  text?: string;
  storyType?: StoryTypeEnum;

  initImageGenerationAt?: FirebaseTimestamp | null;
  deadlineImageGenerationAt?: FirebaseTimestamp | null;

  requestStartTime?: Date | null;
  requestDuration?: number | null;
  midJourneyMode?: MidjourneyModeGeneration | null;

  images?: Array<IPageDataImageModel>;

  customCharacterId?: string | null;
  characterOutlineByUser?: ICharacterOutlineByUser | null;

  block?: boolean; /// just for the trial demo version
  index?: number;
}

export interface IllustrationCharacterModel extends IPageDataModel {
  characterOutlineByUser?: ICharacterOutlineByUser;
  pageIndex?: number | null;
  uploadedImage?: string | null;

  pageData?: IllustrationCharacterPageDataModel;
  index?: number;
  isPortraitDeleted?: boolean;
  newRefImageUploaded?: boolean;
  bookId?: string;
  newUploaded?: boolean;
  currentCharacterPortraitList?: Array<string>;
  characterPortrait?: string;
  /** URL of the newly selected character portrait, used to show updated image immediately */
  newCharacterUrl?: string;
}

export interface IllustrationCharacterPageDataModel {
  imagePromptCharacterUrl?: string;
  uploadedImage?: string | null;
  customCharacterId?: string | null;
  characterOutlineByUser?: ICharacterOutlineByUser | null;
  images?: Array<IPageDataImageModel>;
  scene?: string;
  imageURLwebp?: string;
  index?: number;
  text?: string;
  error?: boolean;
  errorCategory?: ImageGenErrorCategory | null;
  contentFilter?: IPageContentFilterModel | null;
}

export enum StoryCreationMethodEnum {
  AI_ASSISTED = 'ai-assisted',
  SELF_WRITTEN = 'self-written',
}

export interface BookModel {
  id?: string;
  artStyle?: string;
  barCode?: string;
  bookId?: string;
  characterId?: string;
  characterPortrait?: string;
  character_prompt?: string;
  childAge?: string;
  childName?: string;
  coverURL?: string;
  coverURLDesign?: string;
  creditsPerBookLimit?: number;
  creditsPerBookFullLimit?: number;
  email?: string;
  error?: string;
  name?: string;
  imageLibrary?: Array<IImageLibrary>;
  isDraft?: boolean;
  isImageProcessing?: boolean;
  modeCharacterGeneration?: string;
  modeIllustrationGeneration?: string;
  pages?: { [namePage: string]: IPageDataModel };
  pageTotal?: string;
  uploadedImageUrl?: string;
  pdfUrl?: string;
  step?: number;
  storyLanguage?: StoryLanguageTypeEnum;
  author?: string;
  authorName?: string;
  storyTopic?: string;
  storyType?: string;
  summary?: string;
  timestamp?: Date;
  title?: string;
  wordRange?: string;
  writingStyle?: string;
  storyCreationMethod?: StoryCreationMethodEnum;
  illustrationCharacter?: IllustrationCharacterModel | null;
  progress?: number;
  characterOutlineByUser?: ICharacterOutlineByUser;
  currentCharacterPortraitList?: Array<string>;
  shareUrl?: string;

  gettingError?: boolean;
  lastHandledErrorAt?: number;
  uploadedImage?: string | null;
  characterBuilding?: boolean;
  showLibrary?: boolean;
  storybuildingError?: boolean;
  isDummyPic?: boolean;
  archived?: boolean;
  newRefImageUploaded?: boolean;

  // toDo: separate character and book
  age?: string;
  ageUnit?: string;
  mics?: string;
  gender?: string;
  animalType?: string;
  characterType?: string;
  character?: string;
  skinTone?: string;
  hairColor?: string;
  hairStyle?: string;
  eyeColor?: string;
  wearsUses?: string;
  otherFeatures?: string;

  characterRegenerateLimit?: number | null;
  // Set on character image generation FAILURE when the failure was a
  // moderation rejection. Mirrors `pages.{n}.contentFilter` for scenes.
  // The character UI uses truthiness of this field to switch between
  // moderation copy (use `guidance` verbatim) and generic support copy.
  characterContentFilter?: IPageContentFilterModel | null;

  fullBookCompletedAt?: Date | null;
  initCharacterGenerationAt?: FirebaseTimestamp | null;
  deadlineCharacterGenerationAt?: FirebaseTimestamp | null;

  booksCreatedCounter?: number;
  regeneratedNewCharacter?: boolean;

  isDemoBlocked?: boolean;
  showRequireEmailPopup?: boolean;

  // stats
  viewCounter?: number | null,
  purchasedCounter?: number | null,
  purchasedCounterValue?: number | null,
  shippedCounter?: number | null,
  preorder?: number | null,

  // Teacher-specific fields
  classroomId?: string;           // Reference to the classroom this book belongs to
  assignedByTeacher?: string;     // Email of the teacher who assigned this book
  studentName?: string;
  assignmentStatus?: 'assigned' | 'in_progress' | 'completed' | 'reviewed';
  teacherFeedback?: string;       // Feedback from the teacher
  assignedAt?: Date;              // When the book was assigned
  submittedAt?: Date;            // When the student submitted the book
  reviewedAt?: Date;             // When the teacher reviewed the book
  isStudentBook?: boolean;
  // Print-readiness computed by the server at read time (ship-guard rule). Drives the buy
  // screen's blocked state (direct orders) and disclosure note (pooled orders).
  isPrintReady?: boolean;
  printReadyReasons?: string[];
}